import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useGame } from "../context/GameContext";
import PageLayout from "../components/PageLayout";
import MdGlowButton from "../components/MdGlowButton";
import { PlayCircle, Lightbulb } from "lucide-react";

const TUTORIAL_IMAGE_SRC = `${process.env.PUBLIC_URL || ""}/images/tutorial-wagers.png`;

const CommentJouer = () => {
  const navigate = useNavigate();
  const { token } = useGame();

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  return (
    <PageLayout wide>
      <div className="text-center fade-up max-w-2xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-3">
          Comment <span className="text-md-cyan">jouer ?</span>
        </h2>
        <p className="text-white/55 text-sm sm:text-base leading-relaxed">
          La caméra enregistre votre partie du début à la fin. Répondez aux questions, misez sur les bonnes trappes et
          ramenez le plus gros gain possible.
        </p>
      </div>

      <section className="mt-8 max-w-2xl mx-auto space-y-3 text-left fade-up" data-testid="comment-jouer-tutorial">
        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-1">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            1
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="text-white/90">
              Vous disposez de <strong className="text-md-cyan text-base">100 000 €</strong> au départ. À vous de tout
              faire pour les garder.
            </p>
          </div>
        </div>

        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            2
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="text-white/90">
              Vous découvrez <strong className="text-white">4 propositions de réponses</strong>, puis la question.
            </p>
          </div>
        </div>

        <div
          className="md-card p-4 sm:p-5 flex gap-3 border border-amber-400/25 bg-amber-400/5"
          data-testid="comment-jouer-tip-haute-voix"
        >
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400/20 text-amber-300"
            aria-hidden="true"
          >
            <Lightbulb size={15} strokeWidth={2.25} />
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="font-semibold text-amber-200 mb-1">Conseil pour réussir votre casting</p>
            <p className="text-white/80">
              Attention,{" "}
              <strong className="text-white font-bold underline decoration-amber-300/80 underline-offset-2">
                il est impératif de discuter à haute voix
              </strong>
              . Vous êtes physiquement proche l&apos;un de l&apos;autre, vous allez avoir tendance à chuchoter. Si
              hélas, les sons ne sont pas captés, il ne pourra pas y avoir de suite à ce casting.
            </p>
          </div>
        </div>

        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            3
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="text-white/90">
              Vous disposez d&apos;un temps de réflexion <strong className="text-md-cyan">aléatoire</strong>.
            </p>
          </div>
        </div>

        <div
          className="md-card p-4 sm:p-5 flex gap-3 border border-amber-400/25 bg-amber-400/5"
          data-testid="comment-jouer-tip-verbalisation"
        >
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400/20 text-amber-300"
            aria-hidden="true"
          >
            <Lightbulb size={15} strokeWidth={2.25} />
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="font-semibold text-amber-200 mb-1">Conseil pour réussir votre casting</p>
            <p className="text-white/80">
              <strong className="text-white font-bold">
                Vous devez verbaliser votre réflexion durant toute la durée du temps de réflexion
              </strong>
              . Si vous réfléchissez dans votre tête, votre partenaire ne connaîtra ni vos certitudes, ni vos doutes.
              Et surtout, vous passerez à côté de votre casting.
            </p>
          </div>
        </div>

        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-4">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            4
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="text-white/90">
              Vous disposez de <strong className="text-white">45 secondes</strong> pour miser. Dès le départ du
              chronomètre, vous pouvez mettre de l&apos;argent sur les trappes.
            </p>
          </div>
        </div>

        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            5
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0 flex-1">
            <p className="font-semibold text-white mb-1">Comment miser ?</p>
            <p className="text-white/80">
              Sous chaque trappe : cliquez sur <strong className="text-white">+</strong> pour ajouter de l&apos;argent,{" "}
              <strong className="text-white">−</strong> pour retirer de l&apos;argent, <strong className="text-white">Max</strong>{" "}
              pour miser tout l&apos;argent encore disponible dans votre cagnotte.
            </p>
            <img
              src={TUTORIAL_IMAGE_SRC}
              alt="Boutons moins, plus et Max sous une trappe"
              className="mt-4 w-full max-w-xs mx-auto object-contain rounded-xl border border-md-cyan/25 shadow-lg"
              data-testid="comment-jouer-tutorial-image"
            />
            <p className="text-white/80 mt-3">
              Attention, <strong className="text-white">1 trappe</strong> doit rester obligatoirement vide.
            </p>
          </div>
        </div>

        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-6">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            6
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="text-white/90">
              Au terme du chronomètre, vous découvrez la <strong className="text-md-cyan">bonne réponse</strong> à la
              question.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-md mx-auto">
        <MdGlowButton
          onClick={() => navigate("/camera")}
          wrapClassName="mt-8"
          data-testid="comment-jouer-continue-button"
        >
          <PlayCircle className="w-5 h-5 shrink-0" />
          Continuer vers la caméra
        </MdGlowButton>
      </div>
    </PageLayout>
  );
};

export default CommentJouer;
